import { createContext, ReactNode, useCallback, useContext, useState } from 'react'

interface SidebarContextValue {
  isOpen: boolean
  toggle: () => void
  close: () => void
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined)

// Sidebar open-state lives above the router so it survives the home/chat page
// remount — separate routes would otherwise each reset their own copy on mount.
export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  // matchMedia is read synchronously so the first render is already correct (a
  // breakpoint hook reports the base/mobile value until it measures). 48em = `md`.
  const [isOpen, setIsOpen] = useState(() => window.matchMedia('(min-width: 48em)').matches)
  const toggle = useCallback(() => setIsOpen(open => !open), [])
  const close = useCallback(() => setIsOpen(false), [])

  return <SidebarContext.Provider value={{ isOpen, toggle, close }}>{children}</SidebarContext.Provider>
}

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
